import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft, Mail, Phone, MessageCircle } from "lucide-react";

function LegalShell({ title, updated, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-white/10 navy-grain">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4"><Logo light /><Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft size={15} /> Home</Link></div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-10">
        <div className="mb-6 flex flex-wrap gap-2 text-sm">
          {[["/about", "About"], ["/privacy", "Privacy"], ["/terms", "Terms"], ["/refund-policy", "Refund"], ["/faq", "FAQ"]].map(([to, l]) => (
            <NavPill key={to} to={to} label={l} />
          ))}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0B132B]">{title}</h1>
        {updated && <p className="mt-1 text-sm text-slate-400">Last updated: {updated} • Placeholder content — replace with final legal copy.</p>}
        <div className="prose prose-slate mt-6 max-w-none space-y-5 text-sm leading-relaxed text-slate-600">{children}</div>
      </main>
    </div>
  );
}
function NavPill({ to, label }) {
  const loc = useLocation();
  const active = loc.pathname === to;
  return <Link to={to} className={`rounded-md px-3 py-1.5 font-medium ${active ? "bg-[#0B132B] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"}`}>{label}</Link>;
}
const Section = ({ h, children }) => (<div><h2 className="font-display text-lg font-bold text-[#0B132B]">{h}</h2><p className="mt-1.5">{children}</p></div>);

export function About() {
  return (
    <LegalShell title="About ZRo Pe">
      <p>ZRo Pe is a loan and EMI management platform built to help customers in India track, manage and pay their EMIs across multiple lenders from one secure place. Our goal is to make loan management simple, transparent and stress-free.</p>
      <Section h="What we do">We bring your loans together, remind you of upcoming EMIs, offer a secure payment experience and reward you for staying on track. ZRo Pe is a management platform and is not a lender.</Section>
      <Section h="Our principles">We believe in honest communication, privacy by default, and never making unsupported claims about approvals, guarantees or savings.</Section>
      <div className="rounded-xl border border-slate-200 bg-white p-5 not-prose">
        <p className="font-semibold text-[#0B132B]">Contact us</p>
        <div className="mt-3 space-y-2 text-sm text-slate-600">
          <p className="flex items-center gap-2"><Mail size={15} className="text-[#14B8A6]" /> support@zrope.in</p>
          <p className="flex items-center gap-2"><Phone size={15} className="text-[#14B8A6]" /> +91 99999 99999</p>
          <p className="flex items-center gap-2"><MessageCircle size={15} className="text-[#14B8A6]" /> WhatsApp support available</p>
        </div>
      </div>
    </LegalShell>
  );
}
export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated="June 2026">
      <Section h="1. Information we collect">We collect only the information required to provide the relevant feature — such as your name, mobile number and loan references. We do not collect Aadhaar or PAN unless a compliant verification workflow is configured.</Section>
      <Section h="2. How we use your data">Your data is used solely to display and manage your own loans, payments and rewards. It is never used to answer another customer's questions or shared without a lawful basis.</Section>
      <Section h="3. Data security">We apply role-based access control, encrypted sessions and data minimisation. Secrets and credentials are never exposed in the application front-end.</Section>
      <Section h="4. Your rights">You may request access to, correction of, or deletion of your account and data at any time from your Profile.</Section>
      <p className="text-xs text-slate-400">This is placeholder content and should be replaced with your finalised, legally reviewed Privacy Policy.</p>
    </LegalShell>
  );
}
export function Terms() {
  return (
    <LegalShell title="Terms & Conditions" updated="June 2026">
      <Section h="1. Acceptance">By using ZRo Pe you agree to these terms. ZRo Pe is a management platform and does not itself lend money or guarantee any outcome.</Section>
      <Section h="2. Payments">Payments are processed through configured payment partners. Amounts and any fees are shown transparently before you confirm.</Section>
      <Section h="3. Rewards">Reward points and coupons are provided at our discretion and are governed by program rules. Points hold no monetary value unless explicitly stated.</Section>
      <Section h="4. Acceptable use">You agree not to misuse the platform, attempt unauthorised access, or manipulate reward balances.</Section>
      <p className="text-xs text-slate-400">Placeholder content — replace with your finalised Terms & Conditions.</p>
    </LegalShell>
  );
}
export function Refund() {
  return (
    <LegalShell title="Refund Policy" updated="June 2026">
      <Section h="1. EMI payments">EMI payments are made towards your lender's loan account. Refunds for genuine failed or duplicate transactions are processed per the payment partner's timelines, typically 5–7 business days.</Section>
      <Section h="2. Convenience fees">Where a convenience fee applies, it will be shown transparently before you pay. Fees for successful transactions are generally non-refundable.</Section>
      <Section h="3. How to request">Raise a ticket from the Support Center with your transaction ID. Our team will review and respond.</Section>
      <p className="text-xs text-slate-400">Placeholder content — replace with your finalised Refund Policy.</p>
    </LegalShell>
  );
}
export function Faq() {
  const [open, setOpen] = useState(0);
  const items = [
    ["Is ZRo Pe a lender?", "No. ZRo Pe helps you manage and pay existing loans. We are not a lender or NBFC."],
    ["Are payments real?", "Payments are processed through configured payment partners and the amount is always shown before you confirm."],
    ["How does OTP login work?", "We send a one-time passcode to your mobile to verify it's you."],
    ["How are reward points earned?", "Points are credited server-side for actions like paying an EMI on time or playing the daily spin."],
    ["Is my credit score real?", "The credit score shown is indicative until a licensed credit bureau integration is configured."],
    ["How is my data protected?", "We use role-based access, encrypted sessions and only collect what a feature needs."],
  ];
  return (
    <LegalShell title="Frequently Asked Questions">
      <div className="not-prose space-y-3">
        {items.map(([q, a], i) => (
          <div key={q} className="rounded-xl border border-slate-200 bg-white">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between px-5 py-4 text-left font-semibold text-[#0B132B]">{q}<span className="text-[#14B8A6]">{open === i ? "−" : "+"}</span></button>
            {open === i && <p className="border-t border-slate-100 px-5 py-4 text-sm text-slate-600">{a}</p>}
          </div>
        ))}
      </div>
    </LegalShell>
  );
}
